import { Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, FormHelperText, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { financeApi } from "../lib/api";
import type { FeedbackProblemType, FeedbackResponse, User } from "../lib/types";

type FeedbackPageProps = {
  user: User;
};

type FeedbackFormErrors = {
  name?: string;
  message?: string;
};

const MESSAGE_MAX = 1000;

const problemTypeOptions: { value: FeedbackProblemType; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature request" },
  { value: "question", label: "Question" },
  { value: "other", label: "Other" },
];

const SUPPORT_EMAIL = "support@aibros.dev";
const SUPPORT_TELEGRAM = "@aibrosdev";

function validate(form: HTMLFormElement): FeedbackFormErrors {
  const data = new FormData(form);
  const errors: FeedbackFormErrors = {};
  if (!String(data.get("name") || "").trim()) errors.name = "Name is required.";
  const message = String(data.get("message") || "").trim();
  if (!message) {
    errors.message = "Please describe the problem.";
  } else if (message.length > MESSAGE_MAX) {
    errors.message = `Message must be ${MESSAGE_MAX} characters or fewer.`;
  }
  return errors;
}

export function FeedbackPage({ user }: FeedbackPageProps) {
  const [problemType, setProblemType] = useState<FeedbackProblemType>("bug");
  const [messageLength, setMessageLength] = useState(0);
  const [errors, setErrors] = useState<FeedbackFormErrors>({});
  const [result, setResult] = useState<FeedbackResponse | null>(null);

  const mutation = useMutation({
    mutationFn: (form: HTMLFormElement) => {
      const data = new FormData(form);
      return financeApi.submitFeedback({
        problem_type: problemType,
        name: String(data.get("name") || "").trim(),
        telegram: String(data.get("telegram") || "").trim() || null,
        message: String(data.get("message") || "").trim(),
        allow_financial_data_access: data.get("allow_financial_data_access") === "on",
      });
    },
    onSuccess: (response, form) => {
      setResult(response);
      form.reset();
      setMessageLength(0);
      setProblemType("bug");
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;
    setResult(null);
    mutation.mutate(form);
  };

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>Feedback</h1>
          <p>Report a problem or send us a suggestion.</p>
        </Box>
      </header>

      <section className="content">
        <Box className="feedback-stack">
          <Card className="records-panel feedback-info-panel">
            <CardContent className="records-panel-content">
              <h2 className="feedback-section-title">Need help?</h2>
              <p>
                You can email us directly at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or
                reach out on Telegram <strong>{SUPPORT_TELEGRAM}</strong>. Prefer the form? Fill it in
                below and we'll get back to you. A copy of your request will be sent to your account
                email (<strong>{user.email}</strong>).
              </p>
            </CardContent>
          </Card>

          <Card className="records-panel feedback-form-panel">
            <CardContent className="records-panel-content">
              {result ? (
                <Alert severity={result.email_sent ? "success" : "info"} className="feedback-result">
                  Thanks! Your request was received. Reference ID: <strong>{result.reference}</strong>.
                  {result.email_sent
                    ? " A copy has been sent to your email."
                    : " We couldn't email a copy right now, but your request was saved — please keep the reference ID."}
                  {result.allow_financial_data_access
                    ? " Financial data access was explicitly allowed for this request."
                    : " No financial data access was granted."}
                </Alert>
              ) : null}

              {mutation.isError ? (
                <Alert severity="error" className="feedback-result">
                  {mutation.error instanceof Error ? mutation.error.message : "Failed to send feedback."}
                </Alert>
              ) : null}

              <form className="feedback-form" onSubmit={submit} noValidate>
                <TextField
                  select
                  label="Problem type"
                  value={problemType}
                  slotProps={{ select: { native: true } }}
                  onChange={(event) => setProblemType(event.target.value as FeedbackProblemType)}
                >
                  {problemTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>

                <TextField
                  label="Name"
                  name="name"
                  required
                  defaultValue={user.display_name}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                />

                <TextField label="Telegram (optional)" name="telegram" placeholder="@username" />

                <TextField
                  label="Describe the problem"
                  name="message"
                  multiline
                  minRows={4}
                  required
                  slotProps={{ htmlInput: { maxLength: MESSAGE_MAX } }}
                  error={Boolean(errors.message)}
                  helperText={errors.message ?? `${messageLength}/${MESSAGE_MAX}`}
                  onChange={(event) => setMessageLength(event.target.value.length)}
                />

                <Box className="feedback-consent">
                  <FormControlLabel
                    control={<Checkbox name="allow_financial_data_access" />}
                    label="I explicitly allow access to my financial data to solve my case"
                  />
                  <FormHelperText>
                    <strong>Optional.</strong> You can submit feedback without granting access. Leave this unchecked if you do not want support to access your financial data.
                  </FormHelperText>
                </Box>

                <Box className="feedback-actions">
                  <Button variant="contained" type="submit" disabled={mutation.isPending}>
                    {mutation.isPending ? "Sending…" : "Send feedback"}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>
      </section>
    </>
  );
}
